import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { EmailMessage, EmailAttachment } from "@services/documents/email/types";
import { EmailAttachmentChip } from "./EmailAttachmentChip";

interface ForwardedEmailsSectionProps {
  mailAttachments: EmailMessage[];
  onSelectForwardedEmail: (email: EmailMessage) => void;
}

/**
 * Component to display forwarded email attachments with their own attachments
 */
export const ForwardedEmailsSection: React.FC<ForwardedEmailsSectionProps> = ({
  mailAttachments,
  onSelectForwardedEmail,
}) => {
  if (!mailAttachments || mailAttachments.length === 0) {
    return null;
  }

  return (
    <Box mb={2}>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        Forwarded Emails with this email:
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {mailAttachments.map((forwardedEmail: EmailMessage) => {
          const nestedEmailCount =
            forwardedEmail.mailAttachments?.length || 0;

          return (
            <Card
              key={forwardedEmail.messageId}
              variant="outlined"
              onClick={() => onSelectForwardedEmail(forwardedEmail)}
              sx={{
                cursor: "pointer",
                transition: "0.2s",
                "&:hover": { backgroundColor: "#a1d1e0ff" },
              }}
            >
              <CardContent sx={{ p: 1, display: "flex", gap: 1, "&:last-child": { pb: 1 } }}>
                {/* Mail icon on the left */}
                <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                  <EmailIcon fontSize="small" color="action" />
                </Box>

                {/* Email content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Email subject and sender */}
                  <Typography variant="body2" fontWeight="bold" noWrap>
                    {forwardedEmail.subject}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
                    From: {forwardedEmail.fromEmail}
                  </Typography>

                  {/* Show attachments if any */}
                  {forwardedEmail.attachments.length > 0 && (
                    <Box mt={0.5}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {forwardedEmail.attachments.map((attachment: EmailAttachment) => (
                          <EmailAttachmentChip
                            key={attachment.filename}
                            attachment={attachment}
                            messageId={forwardedEmail.messageId}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Show nested email count at the bottom */}
                  {nestedEmailCount > 0 && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: "0.7rem", display: "block", mt: 0.5 }}
                    >
                      (Includes {nestedEmailCount} email{nestedEmailCount > 1 ? "s" : ""})
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
